import { ArrowRight, X } from "lucide-react";
import { Modal } from "@design-lab/shared/ui/modal";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import {
  USER_RETENTION_IMAGE_PATHS,
  USER_RETENTION_CHANGES,
  USER_RETENTION_CHANGES_MOBILE
} from "../constants";

type UserRetentionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onRequestEdits: () => void;
};

export function UserRetentionModal({ isOpen, onClose, onRequestEdits }: UserRetentionModalProps) {
  const { isMobile } = useResponsive();
  const changes = isMobile ? USER_RETENTION_CHANGES_MOBILE : USER_RETENTION_CHANGES;
  const requestEditsButtonText = isMobile
    ? "Use Free Artist Edit"
    : "Request Artist Edits for Free";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc
      closeOnBackdropClick
      title="Design not perfect? We can help!"
      description="Request free artist edits to perfect your design"
      className="mx-auto flex h-auto w-full max-w-[96%] flex-col border-none shadow-none lg:max-w-3xl"
    >
      <div className="flex flex-col">
        <div className="flex items-center justify-between p-3 lg:px-8 lg:pt-7">
          <h3 className="text-base font-bold lg:text-2xl">Design not perfect? We can help!</h3>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer p-0"
            data-testid="close-user-retention-modal-button"
          >
            <X className="size-5 lg:size-6" />
          </button>
        </div>

        <div className="flex flex-col gap-6 p-2 lg:p-8 lg:pt-6">
          <div className="border-3 flex items-center justify-center gap-0 rounded-xl border-gray-200 bg-white py-3 lg:gap-4 lg:p-4">
            <div className="flex w-2/5 flex-col items-center gap-1 lg:gap-4">
              <h5 className="text-xs font-bold text-gray-800 lg:text-sm">Original Upload</h5>
              <img
                src={remoteImageStaticUrl(USER_RETENTION_IMAGE_PATHS.BEFORE)}
                alt="Before"
                className="size-[150px] shrink-0 object-contain lg:size-[200px]"
              />
              <div className="flex flex-col items-start gap-1 lg:gap-2">
                <h5 className="text-xs font-bold text-gray-800 lg:text-sm">
                  Sample message to designer:
                </h5>
                <p className="text-xs text-gray-600 lg:text-sm">
                  Please remove the pink background, change the ribbon color to blue, remove
                  freckles. Change &quot;It&apos;s a girl!&quot; to Emma.
                </p>
              </div>
            </div>

            <div className="flex size-9 items-center justify-center rounded-full bg-black lg:mt-0 lg:size-12">
              <ArrowRight className="size-5 text-white" />
            </div>

            <div className="flex w-2/5 flex-col items-center gap-1 lg:gap-4">
              <h5 className="text-xs font-bold text-gray-800 lg:text-sm">Designer Perfected</h5>
              <img
                src={remoteImageStaticUrl(USER_RETENTION_IMAGE_PATHS.AFTER)}
                alt="After"
                className="size-[150px] shrink-0 object-contain lg:size-[200px]"
              />
              <div className="flex flex-col items-start gap-2">
                {changes.map((change, index) => (
                  <div key={change} className="flex items-center gap-1">
                    <span className="flex size-5 items-center justify-center rounded-full bg-emerald-500 text-sm text-white">
                      {index + 1}
                    </span>
                    <h5 className="text-xs font-bold text-gray-800 lg:text-sm">{change}</h5>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onPress={onRequestEdits}
              size="2xl"
              className="w-[65%] rounded-xl bg-[#2A4DD0] text-base font-bold hover:bg-[#2A4DD0]/90 lg:rounded"
              data-testid="request-artist-edits-for-free"
            >
              {requestEditsButtonText}
            </Button>
            <Button
              onPress={onClose}
              variant="outline"
              size="2xl"
              className="w-[35%] rounded-xl text-base font-bold text-gray-950 lg:rounded"
              data-testid="continue-button"
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
